import React, { useContext, useState, useMemo } from 'react';
import { getBezierPath } from 'reactflow';
import CanvasContext from '../../../context/CanvasContext';
import styles from './styles/Edge.module.css';


export const getEllipsizedText = (text, maxWidth, ctx) => {
    
    const ellipsis = "...";
    let truncatedText = text;
    
    if (!ctx || typeof ctx.measureText !== 'function') {
        console.warn("Canvas context not available for text measurement.");
        return text; 
    }
    try {
        let textWidth = ctx.measureText(truncatedText).width;
        while (textWidth > maxWidth && truncatedText.length > 0) {
            truncatedText = truncatedText.slice(0, -1);
            
            textWidth = ctx.measureText(truncatedText.length < text.length ? truncatedText + ellipsis : truncatedText).width;
        }
        return truncatedText.length < text.length ? truncatedText + ellipsis : text;
    } catch (error) {
        console.error("Error measuring text:", error);
        return text; 
    }
};


const Edge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition = 'right',
    targetPosition = 'left',
    source, 
    target, 
    data, 
    
    
}) => {
    
    
    const { selectedEdgeId, getChoice } = useContext(CanvasContext);
    const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
    

    
    const isLoopEdge = source === target;
    const isSelected = selectedEdgeId === id; 

    
    const [edgePath] = useMemo(() => {
        let path;
        if (isLoopEdge) {
            const radiusY = 50;
            const radiusX = Math.max(20, Math.abs(sourceX - targetX) * 0.6);
            const offset = 5;
            path = `M ${sourceX - offset},${sourceY} A ${radiusX},${radiusY} 0 1,0 ${
                targetX + offset
            },${targetY}`;
        } else {
            
            [path] = getBezierPath({
                sourceX,
                sourceY,
                sourcePosition,
                targetX,
                targetY,
                targetPosition,
            });
        }
        return [path]; 
    }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, source, target, isLoopEdge]);


    
    const handleMouseEnter = () => setHoveredEdgeId(id); 
    const handleMouseLeave = () => setHoveredEdgeId(null);
    const handleEdgeClick = async (event) => { 
        event.stopPropagation();
        await getChoice(id);
    };

    
    const isHovered = hoveredEdgeId === id;
    const hasKeyCondition = data?.showIfKey || data?.hideIfKey;

    
    const edgeStyle = {
        stroke: isSelected ? '#006666' : isHovered ? '#999' : '#000',
        strokeWidth: isSelected ? 2 : isHovered ? 2 : 1,
        fill: 'none',
        cursor: 'pointer',
        strokeDasharray: hasKeyCondition ? '5,5' : undefined,
    };

    const markerId = `arrow-${id}`; 

    
    return (
        <g className={styles.edgePathGroup}>
            {/* Arrowhead Definition */}
            <defs>
                <marker
                    id={markerId}
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                    markerUnits="strokeWidth" 
                >
                    <path
                        d="M0,0 L10,3.5 L0,7 z"
                        fill={edgeStyle.stroke} 
                    />
                </marker>
            </defs>
            {/* Visible Edge Path */}
            <path
                id={`path-${id}`} 
                d={edgePath}
                style={edgeStyle}
                className="react-flow__edge-path" 
                markerEnd={`url(#${markerId})`} 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleEdgeClick}
            />
            {/* Interaction Path (invisible, wider stroke for easier hovering/clicking) */}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={15} 
                className="react-flow__edge-interaction"
                onMouseEnter={handleMouseEnter} 
                onMouseLeave={handleMouseLeave}
                onClick={handleEdgeClick}
                style={{ cursor: 'pointer' }}
            />
            {/**
             * Andrew's to Leon's
             * Description: generate animation along the selected edge(s)
             */}
            {isSelected && !isLoopEdge && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke="#00ff00" 
                    strokeWidth={2}
                    strokeOpacity={0.7}
                    strokeDasharray="5 5"
                    style={{ pointerEvents: 'none' }}
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        values="10;0" 
                        dur="0.5s"
                        repeatCount="indefinite"
                    />
                </path>
            )}
        </g>
    );
};

export default Edge; 